// Candidate intake / profile input. Zod first (per CLAUDE.md conventions);
// the API validates with this, the web form infers its types from it.
import { z } from 'zod';

import { acceptPrivacySchema } from './auth.ts';
import { displayNameSchema } from './text.ts';

import type { CandidateStatus } from './enums.ts';
import { employmentTypeSchema, senioritySchema } from './enums.ts';
import { countryCodeSchema } from './regions.ts';
import { stripContactInfo } from './text.ts';
import { isKnownTimezone } from './timezones.ts';

// Stored in `candidate.availability` (varchar). The ranker gives a small
// bonus to the first two.
export const AVAILABILITIES = [
  'immediately',
  'within_1_month',
  'within_3_months',
  'exploring',
] as const;
const availabilitySchema = z.enum(AVAILABILITIES);
export type Availability = z.infer<typeof availabilitySchema>;

export const currencySchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, 'ISO 4217 code, e.g. USD');

// IANA zone name from the fixed allow-list in timezones.ts (not Intl, which
// accepts names Postgres rejects).
export const timezoneSchema = z
  .string()
  .trim()
  .max(64)
  .refine(isKnownTimezone, { message: 'Unknown IANA timezone' });

/** A profile lists at most this many role types (each with its own seniority). */
export const MAX_ROLE_TARGETS = 5;

// One role type + seniority. Also the body of POST /me/candidate/role-targets
// ("Add this role type" on a role page).
export const roleTargetInputSchema = z.object({
  roleTypeId: z.uuid(),
  seniority: senioritySchema,
});

const skillInputSchema = z.object({
  skillId: z.uuid(),
  years: z.number().int().min(0).max(50).nullable(),
});

const uniqueBy =
  <T>(key: (item: T) => string, label: string) =>
  (items: T[], ctx: z.RefinementCtx) => {
    const seen = new Set<string>();
    for (const item of items) {
      const k = key(item);
      if (seen.has(k)) {
        ctx.addIssue({ code: 'custom', message: `Duplicate ${label}` });
        return;
      }
      seen.add(k);
    }
  };

export const candidateProfileInputSchema = z.object({
  fullName: displayNameSchema,
  country: countryCodeSchema,
  timezone: timezoneSchema,
  employmentTypes: z.array(employmentTypeSchema).min(1).max(3),
  salaryMin: z.number().int().positive().max(10_000_000).nullable(),
  currency: currencySchema.nullable(),
  availability: availabilitySchema,
  roleTargets: z
    .array(roleTargetInputSchema)
    .min(1)
    .max(MAX_ROLE_TARGETS)
    .superRefine(uniqueBy((r) => r.roleTypeId, 'role target')),
  skills: z
    .array(skillInputSchema)
    .max(40)
    .superRefine(uniqueBy((s) => s.skillId, 'skill')),
  industriesWorkedIn: z.array(z.uuid()).max(10),
  industriesInterestedIn: z.array(z.uuid()).max(10),
});
export type CandidateProfileInput = z.infer<typeof candidateProfileInputSchema>;

// Step-by-step onboarding (/profile/<step>). Each step but `review` is saved
// to a server-side draft on "Next"; the candidate row is only created on the
// final submit. The privacy agreement comes with the first step, because that
// is the first time profile data is stored.
export const ONBOARDING_STEPS = ['about', 'wants', 'roles', 'skills', 'review'] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export const onboardingStepSchemas = {
  about: candidateProfileInputSchema
    .pick({ fullName: true, country: true, timezone: true })
    .extend({ acceptPrivacy: acceptPrivacySchema }),
  wants: candidateProfileInputSchema.pick({
    employmentTypes: true,
    salaryMin: true,
    currency: true,
    availability: true,
  }),
  // Industry first: it orders the role type and skill pickers that follow.
  roles: candidateProfileInputSchema.pick({
    industriesWorkedIn: true,
    industriesInterestedIn: true,
    roleTargets: true,
  }),
  skills: candidateProfileInputSchema.pick({ skills: true }),
} as const;

/** Answers saved so far: any subset of the profile fields. */
export type OnboardingDraft = Partial<CandidateProfileInput>;

export const onboardingSubmitSchema = z.object({ turnstileToken: z.string().min(1) });

export const CV_MAX_BYTES = 5 * 1024 * 1024;
export const CV_CONTENT_TYPES = ['application/pdf'] as const;

/**
 * Name to store and show for an uploaded CV: the last path segment, without
 * control characters or contact details (rule 6), at most 120 characters.
 */
export function cleanCvFileName(raw: string | null | undefined): string {
  const base = (raw ?? '').split(/[\\/]/).pop() ?? '';
  const clean = stripContactInfo(base.replace(/\p{Cc}/gu, ''))
    .slice(0, 120)
    .trim();
  return clean || 'cv.pdf';
}

/** Statuses a curator may set by hand (never `new`). */
export const CURATOR_STATUS_TARGETS = [
  'screened',
  'active',
  'paused',
  'withdrawn',
  'placed',
] as const;
export type CuratorStatusTarget = (typeof CURATOR_STATUS_TARGETS)[number];

/** Curator-driven transitions. Server enforces; dashboard uses it to offer buttons. */
export const CANDIDATE_STATUS_TRANSITIONS: Record<CandidateStatus, readonly CuratorStatusTarget[]> =
  {
    new: ['screened', 'active', 'withdrawn'],
    screened: ['active', 'paused', 'withdrawn'],
    active: ['paused', 'withdrawn', 'placed'],
    paused: ['active', 'withdrawn'],
    placed: ['active', 'withdrawn'],
    withdrawn: ['active'],
  };

/** Profiles a curator has not activated yet (screening, Q80 emails). */
export const CANDIDATE_IN_REVIEW_STATUSES = [
  'new',
  'screened',
] as const satisfies readonly CandidateStatus[];

/** Out of the pool: not emailed about skill and role type reviews (Q77/Q79). */
export const CANDIDATE_LEFT_POOL_STATUSES = [
  'withdrawn',
  'placed',
] as const satisfies readonly CandidateStatus[];

export function canCuratorTransition(from: CandidateStatus, to: CuratorStatusTarget): boolean {
  return CANDIDATE_STATUS_TRANSITIONS[from].includes(to);
}
